"use client";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useCartsQuery,
  useUpdateCartItemMutation,
  useRemoveCartItemMutation,
  type Cart,
  type CartItem,
} from "@/hooks/use-carts";
import { toast } from "sonner";
import { CartItemRow } from "@/components/customer/CartItemRow";
import { CartEmptyState } from "@/components/customer/CartEmptyState";
import { CartSummaryBlock } from "@/components/customer/CartSummaryBlock";
import { Skeleton } from "@/components/ui/skeleton";

interface CartSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CartSheet({ open, onOpenChange }: CartSheetProps) {
  const { data: carts, isLoading } = useCartsQuery({ status: "active" });
  const updateItem = useUpdateCartItemMutation();
  const removeItem = useRemoveCartItemMutation();

  const list = (carts ?? []) as Cart[];
  const activeCart = list.length > 0 ? list[0] : null;
  const items = activeCart?.items ?? [];

  const handleQuantityChange = (item: CartItem, newQty: number) => {
    if (!activeCart || newQty < 1) return;
    const qty = Math.max(1, Math.floor(newQty));
    updateItem.mutate(
      {
        cartId: activeCart.id,
        payload: { item_id: item.id, quantity: qty },
      },
      {
        onError: (error: Error) => {
          const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Could not update quantity.";
          toast.error(msg);
        },
      }
    );
  };

  const handleRemove = (item: CartItem) => {
    if (!activeCart) return;
    removeItem.mutate(
      { cartId: activeCart.id, itemId: item.id },
      {
        onError: (error: Error) => {
          const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Could not remove item.";
          toast.error(msg);
        },
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center gap-2 px-4 pr-12 pt-4 pb-3">
          <SheetTitle className="text-base font-semibold">Your Cart</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col min-h-0 overflow-y-auto px-4 py-4 pb-6">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : !activeCart || items.length === 0 ? (
            <div className="flex flex-1 min-h-0 flex-col items-center justify-center text-center">
              <CartEmptyState continueShoppingHref="/customer/dashboard" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <CartItemRow
                    key={item.id}
                    item={item}
                    cartId={activeCart.id}
                    variant="editable"
                    onQuantityChange={handleQuantityChange}
                    onRemove={handleRemove}
                    removePending={removeItem.isPending}
                    quantityUpdatePending={
                      updateItem.isPending &&
                      (updateItem.variables as { payload?: { item_id?: number } } | undefined)?.payload?.item_id === item.id
                    }
                  />
                ))}
              </div>
              <div className="pt-4">
                <CartSummaryBlock
                  total={activeCart.cartTotal}
                  checkoutHref={`/customer/cart/checkout?cart_id=${activeCart.id}`}
                  checkoutLabel="Checkout"
                  compactButtons
                />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
